import { GA4 } from 'src/externals/tag-manager/models/ga4.model';
import { AddBasketLineItemsModel, AddLineItemModel, BasketResponseModel, BasketReturnModel, LineItemReturnModel, OrderReturnModel, ProductCardModel, ProductModel, UpdateShippingModel } from '../services/client.generated';


export namespace MapToGA4 {

    interface Item extends GA4.Item {
        image_url?: string;
    }

    export function search(msg: string): GA4.Search {
        return {
            __event_name: 'search',
            search_term: msg
        };
    }
    function mapGoogleProductCategories(categories: string) {
        let _categories = {};
        if (categories != null && categories.length > 0)
            categories?.split(',')?.forEach((c, i) => _categories['item_category' + (i > 0 ? i + 1 : '')] = c)
        return _categories;
    }
    export function viewCart(response: BasketReturnModel): GA4.ViewCart {
        return {
            __event_name: 'view_cart',
            currency: response?.currency,
            value: response?.total?.amount,
            items: response?.lines?.map(lineItemReturnModel).reduce((accumilator, current) => accumilator.concat(current), []),
            basketId: response?.basketId
        }
    }
    const VARIANT_SEPERATOR = ', ';
    export function itemVariant(item: { selectedOptions?: string[], id?: string, variantOptions?: Array<{ id?: number, title?: string, value?: string }>, options?: { [key: string]: { name?: string } } }): string | null {
        if ('selectedOptions' in item)
            return item?.selectedOptions.join(VARIANT_SEPERATOR);
        if ('variantOptions' in item)
            return (item?.variantOptions ?? []).map(vo => vo.value).join(VARIANT_SEPERATOR);
        if ('options' in item) {
            const vIds = (item.id.split(',').find((_, i) => i > 0) ?? '').split(';').filter(_ => _.length > 0);
            return vIds?.map(id => item.options[id].name)?.join(VARIANT_SEPERATOR);
        }
        return null;
    }
    export function checkoutStart(basket: BasketReturnModel): GA4.BeginCheckout {
        return {
            __event_name: 'begin_checkout',
            currency: basket?.total?.currency,
            value: basket?.total?.amount,
            items: basket?.lines?.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            basketId: basket.basketId
        };
    }
    export function mapProductToView(product: ProductModel): GA4.ViewItem {
        return {
            __event_name: 'view_item',
            value: product?.price?.salesPrice?.amount ?? null,
            currency: product?.price?.salesPrice?.currency ?? null,
            items: productModel(product)
        }
    }
    export function lineItemReturnModel(item: LineItemReturnModel): Array<Omit<Item, '__event_name'>> {
        var price = item.unitPrice?.listPrice?.amount;
        var discount = price - item.unitPrice?.salesPrice?.amount;
        return [{
            item_id: item.sku,
            item_name: item.name,
            item_variant: itemVariant(item),
            image_url: item.imageUrl,
            price,
            discount,
            item_brand: item.brand,
            ...mapGoogleProductCategories(item?.googleProductCategory),
            quantity: item.quantity,
        }]
    }
    export function productCardModel(item: ProductCardModel): Array<Omit<Item, '__event_name'>> {
        var quantity = 1;
        var price = item.price?.listPrice?.amount;
        var discount = price - item.price?.salesPrice?.amount;
        return [{
            item_id: item.id,
            item_name: item.name,
            image_url: item.images.find(() => true),
            quantity,
            item_variant: itemVariant(item),
            price,
            discount,
            item_brand: item.brand,
            ...mapGoogleProductCategories(item?.googleProductCategory),

        }, ...(item?.addons ?? []).map(addon => ({
            item_id: addon.id,
            item_name: addon.name,
            price: item?.price?.salesPrice?.amount,
            image_url: item.images.find(() => true),
            quantity,
            item_variant: itemVariant(addon),
            item_brand: addon.brand,
            ...mapGoogleProductCategories(addon?.googleProductCategory),
        }))]
    }
    export function productModel(item: ProductModel): Array<Omit<Item, '__event_name'>> {
        var price = priceSum(item, 'listPrice');
        var discount = price - priceSum(item, 'salesPrice');
        var quantity = 1;
        return [{
            item_id: item.id,
            item_name: item.name,
            item_variant: itemVariant(item),
            price,
            image_url: item.images.find(() => true),
            quantity,
            discount,
            item_brand: item.brand,
            ...mapGoogleProductCategories(item?.googleProductCategory),
        } as Omit<Item, '__event_name'>, ...(item?.addons ?? []).map(addon => ({
            item_id: addon.id,
            item_name: addon['name'] ?? '',
            item_variant: itemVariant(addon),
            image_url: item.images.find(() => true),
            item_brand: addon.brand,
            quantity,
            ...mapGoogleProductCategories(addon?.googleProductCategory),
        } as Omit<Item, '__event_name'>))]
    }

    export function updateOrCreateLines(response: BasketResponseModel, itemsModel: AddBasketLineItemsModel, pastBasket: BasketReturnModel): GA4.AddToCart | GA4.RemoveFromCart {
        var lineChanges = (response?.basketReturn?.lines
            ?.filter(line => [itemsModel.groupLine, ...(itemsModel?.lines ?? [])].some(_line => _line.sku === line.sku)) || []);
        var replacesLine = pastBasket.lines.find(l => l.id === itemsModel.replaces);
        var quantity = itemsModel.groupLine?.quantity ?? 1;
        if (replacesLine)
            quantity = quantity - replacesLine?.quantity;
        var items = lineChanges
            ?.map(lineItemReturnModel)
            ?.map(lines => lines.map(l => { l.quantity = Math.abs(quantity); return l; }))
            ?.reduce((acc, curr) => acc.concat(curr), []);
        var currency = response?.basketReturn?.currency;
        var value = lineChanges.reduce((accumilator, current) => accumilator + (current.unitPrice.salesPrice.amount * Math.abs(quantity) ?? 0), 0);

        if (replacesLine && quantity < 0) {
            return { __event_name: 'remove_from_cart', items, currency, value }
        }
        return { __event_name: 'add_to_cart', items, currency, value, basketId: response?.basketReturn?.basketId };
    }

    export function updateOrCreateLine(response: BasketResponseModel, itemsModel: AddLineItemModel, pastBasket: BasketReturnModel): GA4.AddToCart | GA4.RemoveFromCart {
        var lineChanges = (response?.basketReturn?.lines
            ?.filter(line => itemsModel.sku === line.sku));
        var currency = response?.basketReturn?.currency;
        var quantityDelta = itemsModel.quantity - pastBasket.lines.find(line => itemsModel.sku === line.sku).quantity;
        var value = lineChanges.reduce((accumilator, current) => accumilator + (current.unitPrice.salesPrice.amount * Math.abs(quantityDelta) ?? 0), 0);
        var items = lineChanges
            ?.map(lineItemReturnModel)
            ?.map(lines => lines.map(l => { l.quantity = Math.abs(quantityDelta); return l; }))
            ?.reduce((acc, curr) => acc.concat(curr), []);
        if (quantityDelta < 0) {
            return { __event_name: 'remove_from_cart', items, currency, value }
        }
        return { __event_name: 'add_to_cart', items, currency, value, basketId: response?.basketReturn?.basketId };
    }

    function priceSum(product: ProductModel, type: string): number {
        return [product?.price[type]?.amount ?? 0, ...(product?.addons ?? []).map(a => a.price[type]?.amount ?? 0)].reduce((a, c) => a + c, 0);
    }
    export function deleteLine(response: BasketResponseModel, ids: string[], pastBasket: BasketReturnModel): GA4.RemoveFromCart {
        const editedLines = (pastBasket?.lines?.filter(line => ids.some(_id => _id === line.id)) || []);
        return {
            __event_name: 'remove_from_cart',
            items: editedLines
                ?.map(lineItemReturnModel)
                ?.reduce((acc, curr) => acc.concat(curr), []),
            currency: response?.basketReturn?.currency,
            value: editedLines.map(l => l.totalPrice.salesPrice.amount).reduce((a, c) => a + c, 0),
            basketId: response?.basketReturn?.basketId
        };
    }
    export function deleteBasket(prev: BasketReturnModel): GA4.RemoveFromCart {
        return {
            __event_name: 'remove_from_cart',
            items: prev?.lines
                ?.map(lineItemReturnModel)
                ?.reduce((acc, curr) => acc.concat(curr), []),
            currency: prev?.currency,
            value: prev?.total?.amount,
            basketId: prev?.basketId
        };
    }

    export function setShipment(basket: BasketReturnModel, shipping: UpdateShippingModel): GA4.AddShippingInfo {
        return {
            __event_name: 'add_shipping_info',
            items: basket.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: basket.total.amount,
            currency: basket.currency,
            shipping_tier: shipping.shippingMethodId,
            basketId: basket?.basketId
        }
    }
    export function setShippingExtras(response: BasketResponseModel, keys: string[], prev: BasketReturnModel): GA4.AddShippingInfo {
        return {
            __event_name: 'add_shipping_info',
            items: response.basketReturn.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: response.basketReturn.total.amount,
            currency: response.basketReturn.currency,
            shipping_tier: response.basketReturn?.shipments?.find(() => true)?.shippingMethodId,
            extras: keys,
            basketId: response?.basketReturn?.basketId
        }
    }
    export function setPayment(basket: BasketReturnModel, paymentId: string): GA4.AddPaymentInfo {
        return {
            __event_name: 'add_payment_info',
            items: basket.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: basket.total.amount,
            currency: basket.currency,
            payment_type: paymentId,
            basketId: basket?.basketId
        }
    }
    export function purchase(response: OrderReturnModel): GA4.Purcahse {
        if (response['IsTracked'] === true)
            return;
        return {
            __event_name: 'purchase',
            items: response.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: response.total.amount,
            currency: response.currency,
            transaction_id: response.orderId,
            order_number: response.orderNumber,
            tax: response.totalVat.amount,
            shipping: response?.shippingTotal?.amount,
            user_data: {
                email: response?.billingAddress?.email,
                phone_number: response?.billingAddress?.phoneNumber,
                address: {
                    first_name:response?.billingAddress?.firstName,
                    last_name: response?.billingAddress?.lastName,
                    city: response?.billingAddress?.city,
                    region: response?.billingAddress?.regionCode,
                    postal_code: response?.billingAddress?.postalCode,
                    country: response?.billingAddress?.country,
                }
            }

            // affiliation: response.
            // coupon: response.
        }
    }
}
